'use client';

import {
  Component,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { isDev } from '../../lib/is-dev';
import { addModalEntry, hasModalOutlet, removeModalEntry } from '../../lib/modal-outlet';
import { SConfirmModal, type SConfirmModalProps } from '../SConfirmModal';
import { SLoadingModal, type SLoadingModalProps } from '../SLoadingModal';

/**
 * 명령형 모달 서비스 — 원본 디자인 시스템의 `sdModal.confirm/loading/create` 파리티.
 * 선언형 <SConfirmModal open> / <SLoadingModal open> 을 대체하는 게 아니라, 버튼 핸들러에서
 * 바로 모달을 띄우는 흐름을 위해 추가로 제공한다.
 *
 * 구현: 호출 시마다 document.body 에 host div 를 만들고 그 안에 모달을 렌더한다.
 * 렌더 경로는 둘 중 하나다.
 *
 * 1. 앱 트리에 `SModalOutlet` 이 있으면 — 전역 스토어에 push 만 하고 outlet 이 host 로
 *    portal 한다. 렌더 트리가 앱의 자식이라 QueryClient·Router·Theme 등 Context 를 전부
 *    상속한다. **권장 경로다.**
 * 2. outlet 이 없으면 — 예전처럼 host 에 `createRoot` 로 별도 루트를 만든다. Context 는
 *    DOM 이 아니라 렌더 트리를 타므로 앱의 Provider 가 하나도 닿지 않는다. 하위 호환용
 *    폴백이며 개발 모드에서 1회 경고한다.
 *
 * 어느 경로든 닫힘 애니메이션이 끝나면 자동으로 언마운트·정리하고, 표시 중 옵션은 마운트
 * 컴포넌트가 state 로 들고 있어 update() 로 갱신할 수 있다.
 */

// 닫힘 정리 지연 — overlay-out(0.3s)/modal-out(0.2s) 중 긴 쪽 + 버퍼
const EXIT_DURATION = 300;
const EXIT_BUFFER = 100;

/** SModal.confirm 옵션 — 제어 흐름을 서비스가 담당하므로 관련 props 는 제외 */
export type SConfirmOptions = Omit<
  SConfirmModalProps,
  'open' | 'onOpenChange' | 'onOk' | 'onCancel' | 'onClose'
>;

/** SModal.loading 옵션 — 제어 흐름을 서비스가 담당하므로 관련 props 는 제외 */
export type SLoadingOptions = Omit<
  SLoadingModalProps,
  'open' | 'onOpenChange' | 'onClose' | 'onButtonClick'
>;

/**
 * create() 가 컴포넌트에 주입하는 prop.
 *
 * 컴포넌트는 `open`/`onOpenChange`/`onClose` 를 루트의 SActionModal 에 **그대로 전달**해야 한다.
 * 전달하지 않으면 모달이 열리지도, 닫히지도 않는다.
 *
 * (컴포넌트 문서 생성기 docs:gen 은 `*Props` interface 를 "컴포넌트"로 취급하므로,
 *  서비스인 SModal 이 컴포넌트로 잘못 문서화되지 않도록 type 별칭으로 둔다. README 는 수작성.)
 */
export type SModalCreateComponentProps = {
  /** 표시 여부 — SActionModal 의 open 에 그대로 전달 */
  open: boolean;
  /** 표시 상태 변경 — SActionModal 의 onOpenChange 에 그대로 전달 */
  onOpenChange: (open: boolean) => void;
  /** 닫기(X) 클릭 — SActionModal 의 onClose 에 그대로 전달 */
  onClose: () => void;
  /** 모달 제어 핸들. 컴포넌트에서 ok()/cancel()/close()/submit()/onSubmit() 호출 */
  modalRef: SModalRef;
};

/**
 * SModal.create 옵션 — SActionModal 을 루트로 렌더하는 컴포넌트를 띄운다.
 *
 * 디자인 시스템이 띄우는 모달은 SActionModal / SConfirmModal / SLoadingModal 세 가지뿐이고,
 * 서비스 메서드가 1:1 로 대응한다 — create → SActionModal, confirm → SConfirmModal,
 * loading → SLoadingModal. create 는 컨테이너를 덧씌우지 않고 component 를 그대로 렌더하며,
 * 표시 제어(open/onOpenChange/onClose/modalRef)만 주입하고 닫힘 후 정리를 담당한다.
 */
export interface SCreateOptions<P extends object = Record<string, never>> {
  /**
   * 띄울 컴포넌트. **루트에 SActionModal 을 렌더**하고 주입받은 open/onOpenChange/onClose 를
   * 그대로 전달해야 한다. 모달 껍데기 없이 본문만 렌더하면 딤·카드 없이 콘텐츠가 그대로
   * 화면에 붙는다 — 개발 모드에서는 이를 감지해 경고한다.
   */
  component: ComponentType<P & SModalCreateComponentProps>;
  /** component 에 전달할 추가 props (주입 prop 과 이름이 겹치면 주입 쪽이 이긴다) */
  componentProps?: P;
}

/**
 * SModal.confirm / loading / create 반환 핸들 — 체이닝 콜백 등록 + 트리거/update/close.
 * 원본 sdModal 의 ModalDialogRef 와 동일하게 단일 타입이며, 모달 종류에 따라 관련 있는
 * 메서드만 실제 발화한다(예: onOk/onCancel 은 confirm, onClick 은 loading, onSubmit 은 create).
 */
export interface SModalRef {
  /** 확인 버튼 클릭 (confirm) 또는 ok() 트리거 */
  onOk(fn: () => void): SModalRef;
  /** 취소(서브) 버튼 클릭 (confirm) 또는 cancel() 트리거 */
  onCancel(fn: () => void): SModalRef;
  /** 닫기(X) 버튼 클릭 또는 close() 트리거 */
  onClose(fn: () => void): SModalRef;
  /**
   * 단일 버튼 모달(loading error 등)의 버튼 클릭. 자동 닫힘이 없으므로
   * consumer 가 update()/close() 로 후속 동작을 결정한다. (원본 sdModal onClick)
   */
  onClick(fn: () => void): SModalRef;
  /**
   * create 커스텀 모달의 제출(submit). 자동 닫힘 없이 fn 만 호출하므로 API 응답을
   * 받은 뒤 ok()/close()/update() 로 후속 동작을 결정한다. (원본 sdModal onSubmit)
   */
  onSubmit(fn: () => void): SModalRef;
  /** 사유와 무관하게 모달이 완전히 닫혀 언마운트된 뒤 (백드롭·ESC 닫힘 포함) */
  onDismissed(fn: () => void): SModalRef;
  /** 표시 중 옵션 갱신 (loading→error, progress, 메시지 등). create 는 미지원. */
  update(patch: Partial<SConfirmOptions & SLoadingOptions>): SModalRef;
  /** onOk 트리거 + 닫기 (저장/처리 성공 시맨틱) */
  ok(): SModalRef;
  /** onCancel 트리거 + 닫기 (작업 취소 시맨틱) */
  cancel(): SModalRef;
  /** onClose 트리거 + 닫기 (중립적 닫기, persistent 여부와 무관) */
  close(): SModalRef;
  /** onSubmit 트리거 (닫힘 없음) — create 커스텀 컴포넌트가 제출 버튼에서 호출 */
  submit(): SModalRef;
}

type OptionsPatch = Partial<SConfirmOptions & SLoadingOptions>;

class SModalRefImpl implements SModalRef {
  private okFn?: () => void;
  private cancelFn?: () => void;
  private closeFn?: () => void;
  private clickFn?: () => void;
  private submitFn?: () => void;
  private dismissedFn?: () => void;
  private closeBinding?: () => void;
  private updateBinding?: (patch: OptionsPatch) => void;
  private pendingClose = false;
  private pendingPatches: OptionsPatch[] = [];
  private dismissRequested = false;
  private torndown = false;
  private root?: Root;
  private host?: HTMLElement;
  private entryId?: number;

  onOk(fn: () => void): this {
    this.okFn = fn;
    return this;
  }
  onCancel(fn: () => void): this {
    this.cancelFn = fn;
    return this;
  }
  onClose(fn: () => void): this {
    this.closeFn = fn;
    return this;
  }
  onClick(fn: () => void): this {
    this.clickFn = fn;
    return this;
  }
  onSubmit(fn: () => void): this {
    this.submitFn = fn;
    return this;
  }
  onDismissed(fn: () => void): this {
    this.dismissedFn = fn;
    return this;
  }
  update(patch: OptionsPatch): this {
    if (this.updateBinding) this.updateBinding(patch);
    else this.pendingPatches.push(patch);
    return this;
  }

  // 닫힘 트리거 — 이미 닫힘이 시작됐으면 무시(중복 콜백 방지)
  private settle(emit: () => void): void {
    if (this.dismissRequested) return;
    this.dismissRequested = true;
    emit();
    if (this.closeBinding) this.closeBinding();
    else this.pendingClose = true;
  }
  ok(): this {
    this.settle(() => this.okFn?.());
    return this;
  }
  cancel(): this {
    this.settle(() => this.cancelFn?.());
    return this;
  }
  close(): this {
    this.settle(() => this.closeFn?.());
    return this;
  }
  submit(): this {
    this.submitFn?.();
    return this;
  }

  /** @internal 마운트 컴포넌트가 닫기 함수를 등록 (등록 전 close() 호출은 지연 반영) */
  _bindClose(fn: () => void): void {
    this.closeBinding = fn;
    if (this.pendingClose) fn();
  }
  /** @internal 마운트 컴포넌트가 옵션 갱신 함수를 등록 (등록 전 update() 호출은 지연 반영) */
  _bindUpdate(fn: (patch: OptionsPatch) => void): void {
    this.updateBinding = fn;
    if (this.pendingPatches.length) {
      this.pendingPatches.forEach(fn);
      this.pendingPatches = [];
    }
  }
  /** @internal host 주입 — 렌더 전에 호출해야 마운트 검사(_getHost)가 성립한다 */
  _setHost(host: HTMLElement): void {
    this.host = host;
  }
  /** @internal outlet 경로 정리 대상(스토어 엔트리) 주입 */
  _setEntry(id: number): void {
    this.entryId = id;
  }
  /** @internal 폴백(createRoot) 경로 정리 대상 주입 */
  _setRoot(root: Root): void {
    this.root = root;
  }
  /** @internal create 의 마운트 검사용 host 조회 */
  _getHost(): HTMLElement | undefined {
    return this.host;
  }
  /** @internal 닫힘 시작 표시 — 컴포넌트 버튼·백드롭·ESC 닫힘 경로가 호출(중복 트리거 방지) */
  _markDismissed(): void {
    this.dismissRequested = true;
  }
  /** @internal confirm 버튼 경로에서 onOk 발화 */ _emitOk(): void {
    this.okFn?.();
  }
  /** @internal confirm 버튼 경로에서 onCancel 발화 */ _emitCancel(): void {
    this.cancelFn?.();
  }
  /** @internal 닫기(X) 경로에서 onClose 발화 */ _emitClose(): void {
    this.closeFn?.();
  }
  /** @internal loading 버튼 경로에서 onClick 발화 */ _emitClick(): void {
    this.clickFn?.();
  }
  /** @internal */ _emitDismissed(): void {
    this.dismissedFn?.();
  }
  /** @internal 닫힘 애니메이션 종료 후 언마운트 */
  _teardown(): void {
    if (this.torndown) return;
    this.torndown = true;
    if (this.entryId !== undefined) {
      // outlet 경로 — host 는 엔트리를 그리던 portal 이 언마운트되며 함께 제거한다
      removeModalEntry(this.entryId);
      return;
    }
    this.root?.unmount();
    this.host?.remove();
  }
  /** @internal 렌더 실패·outlet 소멸로 모달이 사라졌을 때 — 콜백을 마무리하고 정리한다 */
  _fail(): void {
    this.dismissRequested = true;
    this._emitDismissed();
    this._teardown();
  }
}

/** @internal open=false 전환 시 닫힘 애니메이션 후 onDismissed + 언마운트 (1회만) */
function useExitTeardown(open: boolean, refImpl: SModalRefImpl): void {
  const dismissedRef = useRef(false);
  useEffect(() => {
    if (open || dismissedRef.current) return;
    dismissedRef.current = true;
    const t = setTimeout(() => {
      refImpl._emitDismissed();
      refImpl._teardown();
    }, EXIT_DURATION + EXIT_BUFFER);
    return () => clearTimeout(t);
  }, [open, refImpl]);
}

/** @internal SConfirmModal 을 createRoot 로 띄우기 위한 마운트 래퍼 */
function ConfirmMount({ options, refImpl }: { options: SConfirmOptions; refImpl: SModalRefImpl }) {
  // 마운트 즉시 open=true → Radix modal-in 애니메이션 재생
  const [open, setOpen] = useState(true);
  const [opts, setOpts] = useState(options);

  useEffect(() => {
    refImpl._bindClose(() => setOpen(false));
    refImpl._bindUpdate(patch => setOpts(prev => ({ ...prev, ...patch })));
  }, [refImpl]);

  useExitTeardown(open, refImpl);

  // ok/cancel/close 시 SConfirmModal 이 핸들러 호출 후 onOpenChange(false) 로 닫는다.
  return (
    <SConfirmModal
      {...opts}
      open={open}
      onOpenChange={o => {
        if (!o) refImpl._markDismissed();
        setOpen(o);
      }}
      onOk={() => refImpl._emitOk()}
      onCancel={() => refImpl._emitCancel()}
      onClose={() => refImpl._emitClose()}
    />
  );
}

/** @internal SLoadingModal 을 createRoot 로 띄우기 위한 마운트 래퍼 */
function LoadingMount({ options, refImpl }: { options: SLoadingOptions; refImpl: SModalRefImpl }) {
  const [open, setOpen] = useState(true);
  const [opts, setOpts] = useState(options);
  // 버튼 클릭에 이어 오는 onOpenChange(false) 를 자동 닫힘에서 제외하기 위한 플래그
  const buttonClicked = useRef(false);

  useEffect(() => {
    refImpl._bindClose(() => setOpen(false));
    refImpl._bindUpdate(patch => setOpts(prev => ({ ...prev, ...patch })));
  }, [refImpl]);

  useExitTeardown(open, refImpl);

  return (
    <SLoadingModal
      {...opts}
      open={open}
      onOpenChange={o => {
        // 버튼 클릭 후속 onOpenChange(false) 는 무시 — consumer 가 update()/close() 로 결정
        if (!o && buttonClicked.current) {
          buttonClicked.current = false;
          return;
        }
        if (!o) refImpl._markDismissed();
        setOpen(o);
      }}
      onClose={() => refImpl._emitClose()}
      onButtonClick={() => {
        buttonClicked.current = true;
        refImpl._emitClick();
      }}
    />
  );
}

type AnyCreateOptions = SCreateOptions<Record<string, unknown>>;

/** outlet 부재 경고는 앱 전체에서 1회만 — 모달을 열 때마다 콘솔을 채우지 않는다 */
let outletWarned = false;

const NO_OUTLET_CAUSE =
  'SModalOutlet 이 앱 트리에 없어 이 모달은 별도 React 루트(createRoot)로 마운트됩니다. ' +
  'React Context 는 DOM 이 아니라 렌더 트리를 타므로 앱의 Provider(QueryClient·Router·Theme 등)가 ' +
  '닿지 않습니다. 앱 부트스트랩에서 Provider 안쪽에 <SModalOutlet /> 을 한 번 렌더하세요.';

/** @internal 폴백 경로로 마운트할 때의 개발 모드 안내 */
function warnMissingOutlet(): void {
  if (!isDev() || outletWarned) return;
  outletWarned = true;
  console.warn(`[SModal] ${NO_OUTLET_CAUSE}`);
}

/**
 * @internal 마운트 직후 component 가 실제로 모달을 띄웠는지 확인하고, 아니면 개발 모드에서 경고.
 *
 * component 는 임의의 컴포넌트라 정적으로는 판별할 수 없다(TypeScript 도 못 잡는다). 대신 렌더
 * 결과로 판별한다 — 디자인 시스템 모달은 셋 다 Radix Portal 로 body 에 렌더되므로 create 가 만든
 * host 는 비어 있어야 한다. host 에 엘리먼트가 남아 있으면 모달 껍데기 없이 본문만 인라인으로
 * 붙은 것이다.
 */
function warnIfNotModal(host: HTMLElement | undefined, component: unknown): void {
  if (!isDev()) return;
  if (!host || host.childElementCount === 0) return;
  const name = (component as { name?: string })?.name || 'component';
  console.warn(
    `[SModal.create] ${name} 이(가) 모달을 띄우지 않았습니다. create 는 컨테이너를 덧씌우지 ` +
      '않으므로 component 는 루트에 SActionModal 을 렌더하고, 주입받은 open/onOpenChange/onClose 를 ' +
      '그대로 전달해야 합니다. 확인/취소는 SModal.confirm, 로딩은 SModal.loading 을 사용하세요.',
  );
}

/**
 * @internal 모달 컴포넌트를 createRoot 로 띄우기 위한 마운트 래퍼.
 * 컨테이너를 덧씌우지 않고, 표시 제어(open/onOpenChange/onClose)만 주입한다.
 */
function CreateMount({ options, refImpl }: { options: AnyCreateOptions; refImpl: SModalRefImpl }) {
  const { component: Component, componentProps } = options;
  const [open, setOpen] = useState(true);

  useEffect(() => {
    refImpl._bindClose(() => setOpen(false));
    // create: 모달 컴포넌트가 자체 props 로 표시를 결정하므로 update 미지원(no-op)
    refImpl._bindUpdate(() => {});
    // DOM 커밋 후에 검사해야 Portal 이 body 로 빠져나간 결과를 볼 수 있다
    warnIfNotModal(refImpl._getHost(), Component);
  }, [refImpl, Component]);

  useExitTeardown(open, refImpl);

  // componentProps 를 먼저 펼쳐 제어 props(open/onOpenChange/onClose)가 항상 이기게 한다.
  // modalRef 는 children 으로 넘긴 커스텀 본문이 자기 모달을 제어할 수 있도록 함께 주입한다.
  const componentAllProps = {
    ...componentProps,
    open,
    onOpenChange: (o: boolean) => {
      if (!o) refImpl._markDismissed();
      setOpen(o);
    },
    onClose: () => refImpl._emitClose(),
    modalRef: refImpl,
  } as Record<string, unknown> & SModalCreateComponentProps;

  return <Component {...componentAllProps} />;
}

/**
 * @internal 모달 렌더 실패를 모달 안에 가둔다.
 *
 * outlet 경로에서는 모달이 앱 트리의 자식이므로, 경계가 없으면 모달 본문에서 던진 예외가
 * 앱의 상위 boundary 까지 올라가 화면 전체를 무너뜨린다. 폴백 경로에서는 실패 원인이 대개
 * "Provider 가 닿지 않아서"(No QueryClient set 등)이므로 그 사실을 함께 찍어 준다.
 *
 * 클래스 컴포넌트인 이유: 에러 경계는 React 에서 클래스로만 만들 수 있다.
 */
class ModalErrorBoundary extends Component<
  { standalone: boolean; onError: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    console.error(
      this.props.standalone
        ? `[SModal] 모달 렌더에 실패했습니다. ${NO_OUTLET_CAUSE}`
        : '[SModal] 모달 렌더에 실패해 모달을 닫습니다.',
      error,
    );
    // 커밋 중이므로 스토어 갱신·언마운트는 다음 태스크로 미룬다
    setTimeout(() => this.props.onError(), 0);
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * host div 를 만들고 outlet(있으면) 또는 별도 루트(폴백)에 마운트한 뒤 ref 를 반환하는
 * 공통 부트스트랩. 어느 경로든 DOM 상 위치는 body 직속 host 로 동일하다.
 */
function mount(render: (refImpl: SModalRefImpl) => ReactElement): SModalRef {
  const host = document.createElement('div');
  document.body.appendChild(host);

  const refImpl = new SModalRefImpl();
  refImpl._setHost(host);

  const useOutlet = hasModalOutlet();
  const node = (
    <ModalErrorBoundary standalone={!useOutlet} onError={() => refImpl._fail()}>
      {render(refImpl)}
    </ModalErrorBoundary>
  );

  if (useOutlet) {
    // outlet 이 통째로 사라지면(앱 언마운트·Provider 교체) 이 모달을 그려 줄 주체가 없다 —
    // 그때는 렌더 실패와 같이 취급해 onDismissed 를 마무리하고 정리한다
    refImpl._setEntry(addModalEntry(host, node, () => refImpl._fail()));
    return refImpl;
  }

  warnMissingOutlet();
  const root = createRoot(host);
  refImpl._setRoot(root);
  root.render(node);

  return refImpl;
}

/**
 * SModal — 명령형 모달 서비스.
 *
 * @example
 * // 확인/취소
 * SModal.confirm({ type: 'negative', modalTitle: '삭제하시겠습니까?', mainButtonLabel: '삭제', subButtonLabel: '취소' })
 *   .onOk(() => deleteItem())
 *   .onCancel(() => {});
 *
 * @example
 * // 로딩 → 완료 or 에러
 * const ref = SModal.loading({ message: '업로드 중...' });
 * try { await upload(); ref.close(); }
 * catch { ref.update({ state: 'error', message: '업로드 실패' }).onClick(() => retry()); }
 *
 * @example
 * // SActionModal 을 루트로 렌더하는 컴포넌트를 만들어 넘긴다 — 주입 prop 은 그대로 전달
 * function OrderModal({ open, onOpenChange, onClose, modalRef, orderId }: OrderModalProps) {
 *   return (
 *     <SActionModal open={open} onOpenChange={onOpenChange} onClose={onClose}
 *       modalTitle="주문 처리" button={{ label: '처리', onClick: () => modalRef.ok() }}>
 *       <OrderBody orderId={orderId} />
 *     </SActionModal>
 *   );
 * }
 * SModal.create({ component: OrderModal, componentProps: { orderId } }).onOk(() => refetch());
 */
export const SModal = {
  /** 확인/취소 모달을 즉시 띄운다. 반환 핸들에 onOk/onCancel/onClose/onDismissed 체이닝. */
  confirm(options: SConfirmOptions): SModalRef {
    return mount(refImpl => <ConfirmMount options={options} refImpl={refImpl} />);
  },

  /**
   * 로딩(스피너/에러) 모달을 즉시 띄운다. 로딩 중 임의 닫힘 방지를 위해 persistent 기본 true.
   * 버튼 클릭은 자동 닫힘 없이 onClick 만 발화 — update()/close() 로 후속 동작을 결정한다.
   */
  loading(options: SLoadingOptions = {}): SModalRef {
    return mount(refImpl => (
      <LoadingMount options={{ persistent: true, ...options }} refImpl={refImpl} />
    ));
  },

  /**
   * SActionModal 을 루트로 렌더하는 컴포넌트를 띄운다. 컨테이너를 덧씌우지 않고 표시 제어
   * (open/onOpenChange/onClose)와 modalRef 만 주입하며, 닫힘 애니메이션 종료 후 자동으로
   * 언마운트한다. 컴포넌트가 모달을 띄우지 않으면 개발 모드에서 경고한다.
   */
  create<P extends object = Record<string, never>>(options: SCreateOptions<P>): SModalRef {
    return mount(refImpl => (
      <CreateMount options={options as unknown as AnyCreateOptions} refImpl={refImpl} />
    ));
  },
};
